import { useEffect, useRef } from 'react';
import { referralService } from '@/services/referralService';
import { isTierAllowed } from '@/lib/cookie-consent-core';

// Buffer URL params in memory so they survive until consent is given
let bufferedRefCode: string | null = null;
let bufferedHasUTM = false;

// Capture URL params immediately on module load (before any React renders)
// so they survive even if the URL changes before the hook runs
(function captureOnLoad() {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (ref) bufferedRefCode = ref;
  if (params.has('utm_source') || params.has('utm_medium') || params.has('utm_campaign')) {
    bufferedHasUTM = true;
  }
})();

/**
 * Hook to automatically capture and persist referral codes and UTM params.
 * UTM/referral storage is Tier 3 (Marketing) per the Cookie Policy.
 * Params are buffered in memory on arrival and only persisted to localStorage
 * once Tier 3 consent is granted.
 *
 * Pass `tier3Consented` from the consent context so the effect re-runs
 * when the user grants consent mid-session.
 */
export const useReferralTracking = (tier3Consented?: boolean) => {
  const hasPersisted = useRef(false);

  useEffect(() => {
    // Don't persist until tier 3 is allowed
    if (!isTierAllowed(3)) return;
    // Only persist once per session
    if (hasPersisted.current) return;
    hasPersisted.current = true;

    if (bufferedRefCode) {
      referralService.persistReferralCode(bufferedRefCode);
      bufferedRefCode = null;
    }

    if (bufferedHasUTM) {
      referralService.persistUTMParams();
      bufferedHasUTM = false;
    }
  }, [tier3Consented]); // Re-run when consent changes
};
